import React from 'react';
import { Box, Button, IconButton, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';
import { useTranslation } from 'react-i18next';

import { EditorFocus } from '../EditorMode/editorCategories';
import { useCanUseMapEditor } from '../EditorMode/useCanUseMapEditor';

import { useStyles } from './styles';

export interface IEditAreaPickerModalProps {
  open: boolean;
  brandingName?: string;
  onClose: () => void;
  onPick: (focus: EditorFocus) => void;
}

interface FocusOption {
  id: EditorFocus;
  labelKey: string;
  labelFallback: string;
  descriptionKey: string;
  descriptionFallback: string;
  icon: React.ReactNode;
}

const FOCUS_OPTIONS: FocusOption[] = [
  {
    id: 'environment',
    labelKey: 'editorMode.focusEnvironment',
    labelFallback: 'Map & colors',
    descriptionKey: 'editorMode.focusEnvironmentDesc',
    descriptionFallback: 'Theme, palette and environment art',
    icon: <PaletteOutlinedIcon />,
  },
  {
    id: 'spots',
    labelKey: 'editorMode.focusSpots',
    labelFallback: 'Spots & media',
    descriptionKey: 'editorMode.focusSpotsDesc',
    descriptionFallback: 'Images, videos and QR placements',
    icon: <ImageOutlinedIcon />,
  },
  {
    id: 'hotspots',
    labelKey: 'editorMode.focusHotspots',
    labelFallback: 'Hotspots',
    descriptionKey: 'editorMode.focusHotspotsDesc',
    descriptionFallback: 'Interactive 2D panels & 3D objects',
    icon: <PlaceOutlinedIcon />,
  },
  {
    id: 'assistant',
    labelKey: 'editorMode.focusAssistant',
    labelFallback: 'AI scripts & context',
    descriptionKey: 'editorMode.focusAssistantDesc',
    descriptionFallback: 'Guided tours and ask-me-anything',
    icon: <SmartToyOutlinedIcon />,
  },
  {
    id: 'mapEditor',
    labelKey: 'editorMode.focusMapEditor',
    labelFallback: 'Map editor',
    descriptionKey: 'editorMode.focusMapEditorDesc',
    descriptionFallback: 'Build the floor plan from room elements',
    icon: <ViewQuiltOutlinedIcon />,
  },
];

interface ComingSoonOption {
  labelKey: string;
  labelFallback: string;
  descriptionKey: string;
  descriptionFallback: string;
  icon: React.ReactNode;
}

const COMING_SOON_OPTIONS: ComingSoonOption[] = [
  {
    labelKey: 'editorMode.focusObjects',
    labelFallback: 'Objects',
    descriptionKey: 'editorMode.focusObjectsDesc',
    descriptionFallback: 'Place and theme 3D props',
    icon: <ExtensionOutlinedIcon />,
  },
  {
    labelKey: 'editorMode.focusZones',
    labelFallback: 'Zones',
    descriptionKey: 'editorMode.focusZonesDesc',
    descriptionFallback: 'Define triggers and play areas',
    icon: <MapOutlinedIcon />,
  },
];

const EditAreaPickerModal = ({
  open,
  brandingName,
  onClose,
  onPick,
}: IEditAreaPickerModalProps) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const canUseMapEditor = useCanUseMapEditor();

  const focusOptions = canUseMapEditor
    ? FOCUS_OPTIONS
    : FOCUS_OPTIONS.filter((option) => option.id !== 'mapEditor');

  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      sx={classes.modalRoot}
    >
      <Box sx={classes.editAreaContainer}>
        <Box sx={classes.editAreaHeader}>
          <Box sx={classes.editAreaHeaderLeft}>
            <Typography sx={classes.editAreaTitle}>
              {translate(
                'editorMode.editAreaTitle',
                'What do you want to edit?',
              )}
            </Typography>
            {brandingName ? (
              <Typography sx={classes.editAreaSubtitle}>
                {translate('editorMode.editAreaSubtitle', 'Editing {{name}}', {
                  name: brandingName,
                })}
              </Typography>
            ) : null}
          </Box>
          <IconButton onClick={onClose} sx={classes.closeButton} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={classes.editAreaGrid}>
          {focusOptions.map((option) => (
            <Box
              key={option.id}
              sx={classes.editAreaTile}
              onClick={() => onPick(option.id)}
              data-testid={`branding-area-picker-${option.id}-btn`}
            >
              <Box sx={classes.editAreaTileIcon}>{option.icon}</Box>
              <Typography sx={classes.editAreaTileTitle}>
                {translate(option.labelKey, option.labelFallback)}
              </Typography>
              <Typography sx={classes.editAreaTileDescription}>
                {translate(option.descriptionKey, option.descriptionFallback)}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={classes.editAreaComingSoonSection}>
          <Typography sx={classes.editAreaComingSoonHeader}>
            {translate('editorMode.comingSoonHeader', 'Coming soon')}
          </Typography>
          <Box sx={classes.editAreaComingSoonGrid}>
            {COMING_SOON_OPTIONS.map((option) => (
              <Box key={option.labelFallback} sx={classes.editAreaTileDisabled}>
                <Box sx={classes.editAreaTileIconDisabled}>{option.icon}</Box>
                <Box sx={classes.editAreaTileDisabledBody}>
                  <Typography sx={classes.editAreaTileTitleDisabled}>
                    {translate(option.labelKey, option.labelFallback)}
                  </Typography>
                  <Typography sx={classes.editAreaTileDescriptionDisabled}>
                    {translate(
                      option.descriptionKey,
                      option.descriptionFallback,
                    )}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={classes.editAreaFooter}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={classes.cancelButton}
            fullWidth
          >
            {translate('common.cancel', 'Cancel')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditAreaPickerModal;
